import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTour } from "../../lib/useTour";
import { TourSpotlight } from "./TourSpotlight";
import { TourCursor } from "./TourCursor";
import { TourTooltip } from "./TourTooltip";
import { tourSteps } from "./tourSteps";
import "./tour.css";

type Rect = { top: number; left: number; width: number; height: number };

function rectOf(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * The first element matching `selector` that is actually on screen.
 *
 * Two things made the old `querySelector` version wrong on a phone. The app
 * carries the same anchor twice — once in the desktop nav, once in the mobile
 * tab bar — and only one of them is displayed at a given width; querySelector
 * returns whichever comes first in the document, which on a phone was the
 * hidden one. And a `display: none` element still matches, but reports a
 * zero-sized rect, so the tour spotlighted a 0×0 box in the top-left corner
 * rather than falling back to its centred card.
 *
 * Checking size covers both: hidden anchors are skipped, and a step whose
 * target isn't visible anywhere (it lives behind the MORE sheet, say) resolves
 * to nothing and the step narrates from the centre — which is a fine outcome
 * and, crucially, never a broken-looking one.
 */
function firstVisible(selector: string): Element | null {
  for (const el of document.querySelectorAll(selector)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function Tour() {
  const { isOpen, dismiss } = useTour();
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion());
  const timerRef = useRef<number | null>(null);

  // Track reduced-motion changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReducedMotion(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Reset to first step on open
  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      setPaused(false);
    }
  }, [isOpen]);

  // Resolve the current target rect (and re-resolve on resize / scroll)
  const resolveRect = useCallback(() => {
    if (!isOpen) {
      setRect(null);
      return;
    }
    const step = tourSteps[stepIndex];
    if (!step || !step.selector) {
      setRect(null);
      return;
    }
    const el = firstVisible(step.selector);
    if (!el) {
      setRect(null);
      return;
    }
    setRect(rectOf(el));
  }, [isOpen, stepIndex]);

  useEffect(() => {
    resolveRect();
  }, [resolveRect]);

  useEffect(() => {
    if (!isOpen) return;
    let raf = 0;
    const onChange = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(resolveRect);
    };
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [isOpen, resolveRect]);

  // Auto-advance timer
  useEffect(() => {
    if (!isOpen || paused) return;
    const step = tourSteps[stepIndex];
    if (!step) return;
    const dwell = step.dwellMs ?? 8000;
    timerRef.current = window.setTimeout(() => {
      setStepIndex((i) => Math.min(i + 1, tourSteps.length - 1));
    }, dwell);
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [stepIndex, isOpen, paused]);

  // If a step targets an element that isn't in the DOM yet (e.g. recipes still loading),
  // poll briefly so the spotlight can lock on once the element appears. We never force-skip
  // — the tooltip is the important content and shows centered if there's no target.
  useEffect(() => {
    if (!isOpen) return;
    const step = tourSteps[stepIndex];
    if (!step || !step.selector) return;
    if (firstVisible(step.selector)) return;
    let elapsed = 0;
    const interval = window.setInterval(() => {
      elapsed += 200;
      if (step.selector && firstVisible(step.selector)) {
        resolveRect();
        window.clearInterval(interval);
      } else if (elapsed >= 3000) {
        // Give up looking, but don't skip — let the step run with a centered tooltip.
        window.clearInterval(interval);
      }
    }, 200);
    return () => window.clearInterval(interval);
  }, [stepIndex, isOpen, resolveRect]);

  const handleNextOrDone = useCallback(() => {
    if (stepIndex >= tourSteps.length - 1) {
      dismiss();
      return;
    }
    setStepIndex((i) => i + 1);
  }, [stepIndex, dismiss]);

  const handleSkip = useCallback(() => {
    dismiss();
  }, [dismiss]);

  const handlePauseToggle = useCallback(() => {
    setPaused((p) => !p);
  }, []);

  if (!isOpen) return null;

  const step = tourSteps[stepIndex];
  const showCursor = rect !== null;
  const cursorX = rect ? rect.left + rect.width / 2 - 14 : 0;
  const cursorY = rect ? rect.top + rect.height / 2 - 14 : 0;

  // Portalled to <body>: everything here is placed from measured rects and the
  // viewport's own width and height, and the app root is scaled by the reading
  // control (--app-zoom). Inside that subtree those numbers would be scaled a
  // second time and the spotlight would drift off its target; out here they
  // mean what getBoundingClientRect said they meant.
  return createPortal(
    <>
      <TourSpotlight rect={rect} reducedMotion={reducedMotion} />
      {showCursor && <TourCursor x={cursorX} y={cursorY} reducedMotion={reducedMotion} />}
      <TourTooltip
        step={step}
        stepIndex={stepIndex}
        stepCount={tourSteps.length}
        rect={rect}
        paused={paused}
        onPauseToggle={handlePauseToggle}
        onSkip={handleSkip}
        onDone={handleNextOrDone}
      />
    </>,
    document.body
  );
}
